import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Download, ArrowLeft, Send, Check, X, Search, FileText } from "lucide-react";

const inp = "w-full h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#159DFF] disabled:bg-slate-50 disabled:text-slate-500";
const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const addDays = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
const STATUS_CHIP = {
  DRAFT: "bg-slate-100 text-slate-700", SENT: "bg-blue-100 text-blue-700", ACCEPTED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700", EXPIRED: "bg-amber-100 text-amber-700",
};
let keySeq = 1;
const newLine = (svc) => ({
  key: keySeq++, service_id: svc?.id || null, service_name: svc?.name || "", category: svc?.category || null,
  description: svc?.default_description || "", sac_code: svc?.sac_code || "", qty: 1, uom: svc?.uom || "lumpsum",
  rate: "", discount_type: "", discount_value: "", gst_rate: svc?.default_gst_rate || 18,
});

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function QuotationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [catalog, setCatalog] = useState({ items: [], uoms: [] });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null); // loaded quotation (edit/view)

  const [mode, setMode] = useState("PROSPECT");
  const [cust, setCust] = useState({ customer_id: null, name: "", gstin: "", address: "", contact_name: "", phone: "", email: "" });
  const [custQ, setCustQ] = useState("");
  const [custOpts, setCustOpts] = useState([]);
  const [origin, setOrigin] = useState({ name: "", address: "", city: "", pincode: "" });
  const [dest, setDest] = useState({ name: "", address: "", city: "", pincode: "" });
  const [lines, setLines] = useState([]);
  const [validUntil, setValidUntil] = useState(addDays(7));
  const [reverse, setReverse] = useState(false);
  const [notes, setNotes] = useState("");
  const [templates, setTemplates] = useState({});      // terms templates by key
  const [customSections, setCustomSections] = useState(null); // null = auto from selected services
  const [specials, setSpecials] = useState("");

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);

  const readOnly = quote && !["DRAFT", "SENT"].includes(quote.status);

  // catalog
  useEffect(() => {
    api.get("/v2/quotations/catalog").then((r) => setCatalog(r.data)).catch(() => toast.error("Could not load service catalog"));
  }, []);

  // load existing
  useEffect(() => {
    if (!isEdit) { setLines([newLine()]); return; }
    setLoading(true);
    api.get(`/v2/quotations/${id}`).then((r) => {
      const q = r.data;
      setQuote(q);
      const c = q.customer_snapshot || {};
      setMode(q.customer_id ? "EXISTING" : "PROSPECT");
      setCust({ customer_id: q.customer_id || null, name: c.name || "", gstin: c.gstin || "", address: c.address || "",
        contact_name: c.contact_name || "", phone: c.phone || "", email: c.email || "" });
      setOrigin({ name: q.origin?.name || "", address: q.origin?.address || "", city: q.origin?.city || "", pincode: q.origin?.pincode || "" });
      setDest({ name: q.destination?.name || "", address: q.destination?.address || "", city: q.destination?.city || "", pincode: q.destination?.pincode || "" });
      setLines((q.lines || []).map((l) => ({ ...l, key: keySeq++, rate: l.rate, discount_type: l.discount_type || "", discount_value: l.discount_value || "" })));
      setValidUntil(q.valid_until); setReverse(!!q.reverse_charge); setNotes(q.notes || ""); setCustomSections(q.terms_customised ? (q.terms_sections || null) : null); setSpecials((q.special_commitments || []).join("\n"));
    }).catch(() => { toast.error("Quotation not found"); navigate("/admin/quotations"); }).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // default terms for a brand-new quote come from the server on first save; show a hint only
  // customer search
  useEffect(() => {
    if (mode !== "EXISTING" || custQ.trim().length < 2 || cust.customer_id) { setCustOpts([]); return; }
    const t = setTimeout(() => {
      api.get("/v2/quotations/customers", { params: { q: custQ.trim() } }).then((r) => setCustOpts(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [custQ, mode, cust.customer_id]);

  const payload = useMemo(() => ({
    customer: mode === "EXISTING" && cust.customer_id
      ? { customer_id: cust.customer_id, contact_name: cust.contact_name, phone: cust.phone, email: cust.email, address: cust.address }
      : { name: cust.name, gstin: cust.gstin, address: cust.address, contact_name: cust.contact_name, phone: cust.phone, email: cust.email },
    origin: { name: origin.name, address: origin.address, city: origin.city, pincode: origin.pincode },
    destination: { name: dest.name, address: dest.address, city: dest.city, pincode: dest.pincode },
    lines: lines.map((l) => ({
      service_id: l.service_id, service_name: l.service_name, category: l.category, description: l.description,
      sac_code: l.sac_code, qty: Number(l.qty), uom: l.uom, rate: Number(l.rate),
      discount_type: l.discount_type || null, discount_value: Number(l.discount_value) || 0, gst_rate: Number(l.gst_rate),
    })),
    valid_until: validUntil || null, reverse_charge: reverse, notes,
    terms_sections: customSections || undefined,
    special_commitments: specials.split("\n").map((t) => t.trim()).filter(Boolean),
  }), [mode, cust, origin, dest, lines, validUntil, reverse, notes, customSections, specials]);

  // Terms linked to the catalog: COMMON + one section per service category in the quote
  useEffect(() => {
    api.get("/v2/quotations/terms").then((r) => {
      const m = {}; (r.data.items || []).forEach((t) => { m[t.key] = t; }); setTemplates(m);
    }).catch(() => {});
  }, []);
  const TERMS_ORDER = ["COMMON", "COURIER", "CARGO", "PTL", "FTL", "RELOCATION"];
  const autoSections = useMemo(() => {
    const cats = new Set(lines.map((l) => l.category).filter(Boolean));
    return TERMS_ORDER.filter((k) => (k === "COMMON" || cats.has(k)) && templates[k]?.items?.length)
      .map((k) => ({ key: k, title: templates[k].title, items: templates[k].items }));
  }, [lines, templates]); // eslint-disable-line
  const shownSections = readOnly && quote?.terms_sections ? quote.terms_sections : (customSections || autoSections);
  const editSection = (key, text) => {
    const base = customSections || autoSections;
    setCustomSections(base.map((t) => (t.key === key ? { ...t, items: text.split("\n") } : t)));
  };

  const ready = useMemo(() => {
    const custOk = mode === "EXISTING" ? !!cust.customer_id : cust.name.trim().length > 0;
    const linesOk = lines.length > 0 && lines.every((l) => l.service_name.trim() && Number(l.qty) > 0 && l.rate !== "" && Number(l.rate) >= 0);
    return custOk && linesOk;
  }, [mode, cust, lines]);

  // live preview (server is authoritative)
  const seq = useRef(0);
  useEffect(() => {
    if (readOnly) { setPreview(quote ? { tax_mode: quote.tax_mode, totals: quote.totals, lines: quote.lines, billing_gstin: quote.billing_gstin } : null); return; }
    if (!ready) { setPreview(null); setPreviewErr(""); return; }
    const mine = ++seq.current;
    setPreviewBusy(true);
    const t = setTimeout(() => {
      const { terms_sections: _t, special_commitments: _s, ...rest } = payload;
      api.post("/v2/quotations/preview", rest).then((r) => {
        if (mine !== seq.current) return;
        setPreview(r.data); setPreviewErr("");
      }).catch((e) => {
        if (mine !== seq.current) return;
        const d = e.response?.data?.detail;
        setPreviewErr(typeof d === "string" ? d : "Check the highlighted fields"); setPreview(null);
      }).finally(() => { if (mine === seq.current) setPreviewBusy(false); });
    }, 400);
    return () => clearTimeout(t);
  }, [payload, ready, readOnly]); // eslint-disable-line

  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const grouped = useMemo(() => {
    const g = {};
    (catalog.items || []).forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [catalog]);

  const pickCustomer = (c) => {
    setCust({ customer_id: c.customer_id, name: c.name, gstin: c.gstin, address: c.address, contact_name: c.contact_name, phone: c.phone, email: c.email });
    setCustQ(c.name); setCustOpts([]);
  };

  const downloadPdf = async (qid, number) => {
    try {
      const r = await api.get(`/v2/quotations/${qid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Sendtoo_Quote_${(number || "").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const save = async (after) => {
    if (!ready) { toast.error("Add a customer and at least one complete line (service, qty, rate)"); return; }
    setSaving(true);
    try {
      const r = isEdit ? await api.put(`/v2/quotations/${id}`, payload) : await api.post("/v2/quotations", payload);
      let q = r.data;
      if (after === "send" && q.status === "DRAFT") q = (await api.post(`/v2/quotations/${q.id}/status`, { status: "SENT" })).data;
      toast.success(`Saved ${q.quote_number}`);
      if (after === "pdf") await downloadPdf(q.id, q.quote_number);
      if (!isEdit) navigate(`/admin/quotations/${q.id}`, { replace: true });
      else setQuote({ ...quote, ...q });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save quotation");
    } finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    try {
      const r = await api.post(`/v2/quotations/${id}/status`, { status });
      setQuote((q) => ({ ...q, status: r.data.status })); toast.success(`Marked ${status.toLowerCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Status change failed"); }
  };

  const clone = async () => {
    try { const r = await api.post(`/v2/quotations/${id}/clone`); toast.success(`Cloned as ${r.data.quote_number}`); navigate(`/admin/quotations/${r.data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
  };

  if (loading) return <DashboardLayout title="Quotation"><div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></DashboardLayout>;

  const totals = preview?.totals;
  const taxLabel = preview ? (preview.tax_mode === "CGST_SGST" ? "CGST + SGST" : "IGST") : null;

  return (
    <DashboardLayout title="Quotation">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="quotation-form">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button onClick={() => navigate("/admin/quotations")} className="text-slate-500 hover:text-slate-800"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{isEdit ? quote?.quote_number : "New quotation"}</h1>
          {quote && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CHIP[quote.status]}`}>{quote.status}</span>}
          <div className="ml-auto flex flex-wrap gap-2">
            {isEdit && <Button variant="outline" size="sm" onClick={() => downloadPdf(id, quote?.quote_number)}><Download className="w-4 h-4 mr-1.5" />PDF</Button>}
            {isEdit && <Button variant="outline" size="sm" onClick={clone}><Copy className="w-4 h-4 mr-1.5" />Clone</Button>}
            {isEdit && quote?.status === "ACCEPTED" && (
              <Button size="sm" className="bg-[#159DFF] hover:bg-[#0f86dd] text-white" data-testid="convert-to-invoice"
                onClick={() => navigate("/admin/invoices/new", { state: { fromQuote: quote } })}>
                <FileText className="w-4 h-4 mr-1.5" />Convert to invoice
              </Button>
            )}
            {isEdit && quote?.status === "SENT" && <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus("ACCEPTED")}><Check className="w-4 h-4 mr-1.5" />Accepted</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => changeStatus("REJECTED")}><X className="w-4 h-4 mr-1.5" />Rejected</Button>
            </>}
          </div>
        </div>

        {(quote?.converted_invoices || []).length > 0 && (
          <p className="mb-4 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2" data-testid="quote-invoiced-note">
            Invoiced:{" "}
            {quote.converted_invoices.map((c, i) => (
              <button key={c.invoice_number + i} type="button" className="underline font-semibold mr-2" onClick={() => navigate(`/admin/invoices/${c.invoice_number}`)}>{c.invoice_number}</button>
            ))}
          </p>
        )}

        <div className="grid lg:grid-cols-3 gap-5">
          <fieldset disabled={!!readOnly} className="lg:col-span-2 space-y-5 min-w-0">
            {/* Customer */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Customer</h2>
                <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs font-semibold">
                  {["EXISTING", "PROSPECT"].map((m) => (
                    <button key={m} type="button" onClick={() => { setMode(m); setCust({ customer_id: null, name: "", gstin: "", address: "", contact_name: "", phone: "", email: "" }); setCustQ(""); }}
                      className={`px-3 py-1.5 ${mode === m ? "bg-[#0A183D] text-white" : "bg-white text-slate-600"}`}>{m === "EXISTING" ? "Existing customer" : "New prospect"}</button>
                  ))}
                </div>
              </div>
              {mode === "EXISTING" && (
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <input className={inp + " pl-8"} placeholder="Search name, mobile, email, GSTIN or customer ID" value={custQ}
                    onChange={(e) => { setCustQ(e.target.value); setCust((c) => ({ ...c, customer_id: null })); }} data-testid="quote-customer-search" />
                  {custOpts.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                      {custOpts.map((c) => (
                        <button key={c.customer_id} type="button" onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                          <div className="text-sm font-semibold text-slate-800">{c.name}{c.customer_code && <span className="ml-2 font-mono text-xs text-slate-500">#{c.customer_code}</span>}</div>
                          <div className="text-xs text-slate-500">{[c.contact_name, c.phone, c.gstin].filter(Boolean).join(" · ")}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name / company"><input className={inp} value={cust.name} disabled={mode === "EXISTING"} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="quote-cust-name" /></Field>
                <Field label="GSTIN (blank if unregistered)"><input className={inp} maxLength={15} value={cust.gstin} disabled={mode === "EXISTING"} onChange={(e) => setCust({ ...cust, gstin: e.target.value.toUpperCase() })} placeholder="37AANCC5387L1ZV" /></Field>
                <Field label="Contact person"><input className={inp} value={cust.contact_name} onChange={(e) => setCust({ ...cust, contact_name: e.target.value })} /></Field>
                <Field label="Phone"><input className={inp} value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></Field>
                <Field label="Email"><input className={inp} value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} /></Field>
                <Field label="Bill-to address"><textarea rows={3} className={inp + " h-auto py-2"} value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} data-testid="quote-bill-address" /></Field>
              </div>
              {taxLabel && <p className="mt-3 text-xs text-slate-500">Tax type (automatic): <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{taxLabel}</span></p>}
            </section>

            {/* Ship from / Ship to */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Ship from &amp; Ship to</h2>
              <div className="grid md:grid-cols-2 gap-5">
                {[["Ship from", origin, setOrigin, "from"], ["Ship to", dest, setDest, "to"]].map(([title, v, set, k]) => (
                  <div key={k} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-600 uppercase">{title}</p>
                      <button type="button" className="text-xs text-[#159DFF] hover:underline"
                        onClick={() => set({ ...v, name: cust.name, address: cust.address })}>Copy from Bill-to</button>
                    </div>
                    <Field label="Name / company (optional)"><input className={inp} value={v.name} onChange={(e) => set({ ...v, name: e.target.value })} data-testid={`quote-${k}-name`} /></Field>
                    <Field label="Address"><textarea rows={3} className={inp + " h-auto py-2"} value={v.address} onChange={(e) => set({ ...v, address: e.target.value })} data-testid={`quote-${k}-address`} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="City"><input className={inp} value={v.city} onChange={(e) => set({ ...v, city: e.target.value })} /></Field>
                      <Field label="Pincode"><input className={inp} maxLength={6} value={v.pincode} onChange={(e) => set({ ...v, pincode: e.target.value.replace(/\D/g, "") })} /></Field>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Lines */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Services</h2>
                <div className="ml-auto flex gap-2">
                  <select className={inp + " w-auto"} value="" onChange={(e) => { const s = catalog.items.find((i) => i.id === e.target.value); if (s) setLines((ls) => [...ls, newLine(s)]); }} data-testid="quote-add-service">
                    <option value="">+ Add service from catalog…</option>
                    {Object.keys(grouped).map((cat) => (
                      <optgroup key={cat} label={cat}>{grouped[cat].map((s) => <option key={s.id} value={s.id}>{s.name} ({s.default_gst_rate}%)</option>)}</optgroup>
                    ))}
                  </select>
                  <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, newLine()])}><Plus className="w-4 h-4 mr-1" />Custom</Button>
                </div>
              </div>
              <div className="space-y-3">
                {lines.map((l, idx) => {
                  const calc = preview?.lines?.[idx];
                  return (
                    <div key={l.key} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`quote-line-${idx}`}>
                      <div className="grid sm:grid-cols-12 gap-2">
                        <Field label="Service" className="sm:col-span-5"><input className={inp} value={l.service_name} onChange={(e) => setLine(l.key, { service_name: e.target.value })} /></Field>
                        <Field label="SAC" className="sm:col-span-2"><input className={inp} value={l.sac_code} onChange={(e) => setLine(l.key, { sac_code: e.target.value })} /></Field>
                        <Field label="Qty" className="sm:col-span-2"><input type="number" min="0" step="any" className={inp} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></Field>
                        <Field label="Unit" className="sm:col-span-3">
                          <select className={inp} value={l.uom} onChange={(e) => setLine(l.key, { uom: e.target.value })}>{(catalog.uoms || []).map((u) => <option key={u}>{u}</option>)}</select>
                        </Field>
                        <Field label="Description" className="sm:col-span-12"><textarea rows={2} className={inp + " h-auto py-1.5"} value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} placeholder="What is included, vehicle type, route, conditions…" /></Field>
                        <Field label="Rate (₹)" className="sm:col-span-3"><input type="number" min="0" step="any" className={inp} value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} /></Field>
                        <Field label="Discount" className="sm:col-span-4">
                          <div className="flex gap-1.5">
                            <select className={inp + " w-24"} value={l.discount_type} onChange={(e) => setLine(l.key, { discount_type: e.target.value })}>
                              <option value="">None</option><option value="PERCENT">%</option><option value="AMOUNT">₹</option>
                            </select>
                            <input type="number" min="0" step="any" className={inp} disabled={!l.discount_type} value={l.discount_value} onChange={(e) => setLine(l.key, { discount_value: e.target.value })} />
                          </div>
                        </Field>
                        <Field label="GST" className="sm:col-span-3">
                          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm font-semibold h-9">
                            {[5, 18].map((g) => (
                              <button key={g} type="button" onClick={() => setLine(l.key, { gst_rate: g })} data-testid={`quote-gst-${idx}-${g}`}
                                className={`px-4 ${Number(l.gst_rate) === g ? "bg-[#159DFF] text-white" : "bg-white text-slate-600"}`}>{g}%</button>
                            ))}
                          </div>
                        </Field>
                        <div className="sm:col-span-2 flex items-end justify-between gap-2">
                          <div className="text-right flex-1">
                            <div className="text-[11px] uppercase font-semibold text-slate-400">Line total</div>
                            <div className="text-sm font-bold text-slate-900">₹{fmt(calc?.line_total)}</div>
                          </div>
                          {!readOnly && lines.length > 1 && <button type="button" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="text-slate-400 hover:text-red-600 pb-1.5" aria-label="Remove line"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Terms */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 grid sm:grid-cols-2 gap-3">
              <Field label="Valid until"><input type="date" className={inp} min={addDays(0)} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-5">
                <input type="checkbox" checked={reverse} onChange={(e) => setReverse(e.target.checked)} className="w-4 h-4" />
                Reverse charge applies (tax payable by recipient)
              </label>
              <Field label="Notes" className="sm:col-span-2"><textarea rows={2} className={inp + " h-auto py-1.5"} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            </section>

            {/* Special commitments */}
            <section className="bg-amber-50 border border-amber-300 rounded-xl p-4 sm:p-5" data-testid="quote-specials">
              <h2 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-1">Special commitments (as discussed)</h2>
              <p className="text-xs text-amber-800 mb-2">Anything agreed with this client during negotiation, one per line. Printed in a highlighted box above the terms and prevails over the standard terms.</p>
              <textarea rows={4} className={inp + " h-auto py-2 bg-white"} value={specials} onChange={(e) => setSpecials(e.target.value)}
                placeholder={"e.g. Detention free for 48 hours at loading point\nPayment within 30 days of invoice\nRate held for 3 months"} />
            </section>

            {/* Terms & conditions (auto-selected by service) */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid="quote-terms">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Terms &amp; conditions</h2>
                <span className="text-xs text-slate-500">{customSections ? "Customised for this quote" : "Auto-selected from the services above"}</span>
                {customSections && !readOnly && <button type="button" className="ml-auto text-xs text-[#159DFF] hover:underline" onClick={() => setCustomSections(null)}>Reset to standard terms</button>}
              </div>
              <p className="text-xs text-slate-500 mb-3">Standard terms are managed in Service catalog → Terms templates. Edit below only for this quote (one point per line).</p>
              <div className="space-y-3">
                {shownSections.map((t) => (
                  <div key={t.key}>
                    <p className="text-xs font-bold text-slate-600 mb-1">{t.title}</p>
                    <textarea rows={Math.min(10, Math.max(3, (t.items || []).length))} className={inp + " h-auto py-1.5 text-xs"}
                      value={(t.items || []).join("\n")} onChange={(e) => editSection(t.key, e.target.value)} />
                  </div>
                ))}
                {shownSections.length === 0 && <p className="text-sm text-slate-400">Add a service to see its terms.</p>}
              </div>
            </section>
          </fieldset>

          {/* Totals */}
          <aside className="lg:sticky lg:top-4 h-fit space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-testid="quote-totals">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Summary</h2>
                {previewBusy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
              {!totals && <p className="text-sm text-slate-400">{previewErr || "Add a customer and complete at least one service line to see the totals."}</p>}
              {totals && (
                <dl className="text-sm space-y-1.5">
                  <Row k="Subtotal" v={totals.subtotal} />
                  {totals.total_discount > 0 && <Row k="Discount" v={-totals.total_discount} />}
                  <Row k="Taxable value" v={totals.taxable_total} />
                  {totals.tax_summary_by_rate.map((s) => preview.tax_mode === "CGST_SGST" ? (
                    <div key={s.rate}><Row k={`CGST @ ${s.rate / 2}%`} v={s.cgst} muted /><Row k={`SGST @ ${s.rate / 2}%`} v={s.sgst} muted /></div>
                  ) : <Row key={s.rate} k={`IGST @ ${s.rate}%`} v={s.igst} muted />)}
                  {totals.round_off !== 0 && <Row k="Round off" v={totals.round_off} muted />}
                  <div className="flex justify-between pt-3 mt-2 border-t border-slate-200 text-base font-bold text-slate-900">
                    <dt>Grand total</dt><dd data-testid="quote-grand-total">₹{fmt(totals.grand_total)}</dd>
                  </div>
                </dl>
              )}
              {previewErr && totals && <p className="text-xs text-red-600 mt-2">{previewErr}</p>}
            </div>
            {!readOnly && (
              <div className="space-y-2">
                <Button className="w-full bg-[#159DFF] hover:bg-[#0F84D9] text-white" disabled={saving || !ready} onClick={() => save()} data-testid="quote-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Save changes" : "Save as draft"}
                </Button>
                <Button variant="outline" className="w-full" disabled={saving || !ready} onClick={() => save("pdf")}><Download className="w-4 h-4 mr-1.5" />Save & download PDF</Button>
                {(!isEdit || quote?.status === "DRAFT") && <Button variant="outline" className="w-full" disabled={saving || !ready} onClick={() => save("send")}><Send className="w-4 h-4 mr-1.5" />Save & mark as sent</Button>}
              </div>
            )}
            {readOnly && <p className="text-xs text-slate-500">This quotation is {quote.status.toLowerCase()} and can't be edited. Use Clone to make a new version.</p>}
            {quote?.events?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">History</h3>
                <ol className="space-y-1.5">
                  {quote.events.map((e, i) => (
                    <li key={i} className="text-xs text-slate-600"><span className="font-semibold text-slate-800">{e.event}</span> · {e.by} · {new Date(e.at).toLocaleString("en-IN")}</li>
                  ))}
                </ol>
              </div>
            )}
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-slate-500" : "text-slate-700"}`}>
      <dt>{k}</dt><dd className="tabular-nums">{v < 0 ? "- " : ""}₹{fmt(Math.abs(v))}</dd>
    </div>
  );
}
